/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        UnauthorizedPage.tsx
 * @description Page d'erreur 403 - Acces non autorise
 *              Affichee lorsqu'un utilisateur tente d'acceder a une ressource
 *              pour laquelle il n'a pas les permissions requises
 *
 * @version     2.5
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useNavigate } from 'react-router-dom';
import { ShieldExclamationIcon, HomeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '@/store/authStore';

/**
 * Page d'erreur 403 - Acces non autorise
 *
 * Affiche un message explicatif et propose des actions a l'utilisateur :
 * - Retourner a la page precedente
 * - Retourner au tableau de bord
 * - Se deconnecter pour changer de compte
 */
export function UnauthorizedPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    // Rediriger vers l'interface appropriee selon le role
    if (user?.roles.includes('ROLE_AGENT_DENEIGNEMENT') &&
        !user?.roles.includes('ROLE_CHEF_DENEIGNEMENT') &&
        !user?.roles.includes('ROLE_GID_REGUL') &&
        !user?.roles.includes('ROLE_ADMIN')) {
      navigate('/agent');
    } else {
      navigate('/');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="max-w-md w-full text-center">
        {/* Icone */}
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-6">
          <ShieldExclamationIcon className="w-12 h-12 text-red-600" />
        </div>

        {/* Titre */}
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Acces non autorise
        </h1>

        {/* Code erreur */}
        <p className="text-6xl font-extrabold text-red-600 mb-4">
          403
        </p>

        {/* Message */}
        <p className="text-gray-600 mb-8">
          Vous n'avez pas les permissions necessaires pour acceder a cette page.
          {user && (
            <span className="block mt-2 text-sm text-gray-500">
              Connecte en tant que : <strong>{user.nomComplet}</strong>
            </span>
          )}
        </p>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleGoBack}
            className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            Retour a la page precedente
          </button>

          <button
            onClick={handleGoHome}
            className="w-full flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            Retour au tableau de bord
          </button>

          <button
            onClick={handleLogout}
            className="w-full px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Se connecter avec un autre compte
          </button>
        </div>

        {/* Footer */}
        <p className="mt-8 text-xs text-gray-400">
          Si vous pensez qu'il s'agit d'une erreur, contactez votre administrateur.
        </p>
      </div>
    </div>
  );
}

export default UnauthorizedPage;
